import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, ArrowRight, ClipboardCheck, Clock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceService } from '@/services/compliance-service';
import type { AssetCompliance } from '@/types/compliance';
import { formatDate, cn } from '@/lib/utils';

type OverduePriority = 'Critical' | 'High' | 'Medium';

function getOverduePriority(daysOverdue: number): OverduePriority {
  if (daysOverdue > 30) return 'Critical';
  if (daysOverdue > 14) return 'High';
  return 'Medium';
}

interface OverdueInspection {
  id: number;
  assetId: string;
  assetName: string;
  assetCode: string;
  regimeName: string;
  nextInspectionDate: string;
  daysOverdue: number;
  priority: OverduePriority;
}

export function OverdueInspectionsWidget() {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<OverdueInspection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOverdueInspections();
  }, []);

  const loadOverdueInspections = async () => {
    try {
      setLoading(true);
      const compliances = await complianceService.getAssetCompliances();
      
      // Filter for overdue inspections and exclude disposed/sold assets
      const overdue = compliances
        .filter((item: AssetCompliance) => {
          if (!item.nextInspectionDate) return false;
          if (item.assetStatus === 'Disposed' || item.assetStatus === 'Sold') return false;
          
          const daysOverdue = Math.floor(
            (new Date().getTime() - new Date(item.nextInspectionDate).getTime()) / (1000 * 60 * 60 * 24)
          );
          
          return daysOverdue > 0;
        })
        .map((item: AssetCompliance) => {
          const daysOverdue = Math.floor(
            (new Date().getTime() - new Date(item.nextInspectionDate!).getTime()) / (1000 * 60 * 60 * 24)
          );
          
          const getPriority = (days: number): OverduePriority => {
            if (days > 30) return 'Critical';
            if (days > 14) return 'High';
            return 'Medium';
          };
          
          return {
            id: item.id,
            assetId: item.assetId,
            assetName: item.assetName,
            assetCode: item.assetCode,
            regimeName: item.regimeName,
            nextInspectionDate: item.nextInspectionDate!,
            daysOverdue: daysOverdue,
            priority: getOverduePriority(daysOverdue),
          } as OverdueInspection;
        })
        .sort((a: OverdueInspection, b: OverdueInspection) => b.daysOverdue - a.daysOverdue)
        .slice(0, 5); // Show top 5

      setInspections(overdue);
    } catch (error) {
      console.error('Failed to load overdue inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return 'destructive';
      case 'High':
        return 'destructive';
      case 'Medium':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const handleViewAll = () => {
    navigate('/compliance/assets?filter=overdue');
  };

  const handleInspectionClick = (inspection: OverdueInspection) => {
    navigate(`/assets/${inspection.assetId}`);
  };

  if (loading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Overdue Inspections</CardTitle>
              <CardDescription>Inspections past due date</CardDescription>
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-950 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (inspections.length === 0) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Overdue Inspections</CardTitle>
              <CardDescription>Inspections past due date</CardDescription>
            </div>
            <div className="p-2 bg-green-50 dark:bg-green-950 rounded-lg">
              <ClipboardCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ClipboardCheck className="h-12 w-12 text-green-600 dark:text-green-400 mb-3" />
            <p className="text-sm font-medium text-green-600 dark:text-green-400">
              All inspections are up to date!
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              No overdue inspections at this time
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col')}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold tracking-tight">Overdue Inspections</CardTitle>
              <Badge variant="destructive" className="text-xs">
                {inspections.length}
              </Badge>
            </div>
            <CardDescription>Inspections past due date</CardDescription>
          </div>
          <div className="p-2 bg-red-50 dark:bg-red-950 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {inspections.map((inspection) => (
          <button
            key={inspection.id}
            type="button"
            className="w-full flex items-start justify-between gap-3 p-3 rounded-lg border border-destructive/20 bg-destructive/5 hover:bg-destructive/10 transition-colors cursor-pointer text-left"
            onClick={() => handleInspectionClick(inspection)}
          >
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium truncate">
                  {inspection.assetName}
                </p>
                <Badge variant={getPriorityColor(inspection.priority)} className="text-xs shrink-0">
                  {inspection.priority}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {inspection.regimeName}
              </p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Due: {formatDate(inspection.nextInspectionDate)}
                </span>
                <span className="flex items-center gap-1 text-destructive font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  {inspection.daysOverdue} {inspection.daysOverdue === 1 ? 'day' : 'days'} overdue
                </span>
              </div>
            </div>
          </button>
        ))}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAll}
          className="w-full mt-2"
        >
          View All Overdue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
